import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import providerService from '../../services/providerService';

const getProviderErrorPayload = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const detailedMessage =
    responseData?.message ||
    responseData?.error ||
    responseData?.sqlError ||
    error?.message;

  const message = typeof detailedMessage === 'string' && detailedMessage.trim()
    ? detailedMessage
    : fallbackMessage;

  return {
    message,
    field: responseData?.field || null,
    errors: responseData?.errors || null
  };
};

const getProviderErrorMessage = (error, fallbackMessage) =>
  getProviderErrorPayload(error, fallbackMessage).message;

// Async thunks
export const createProvider = createAsyncThunk(
  'provider/createProvider',
  async ({ practiceId, providerData }, { rejectWithValue }) => {
    try {
      const response = await providerService.createProvider(practiceId, providerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getProviderErrorPayload(error, 'Failed to create provider'));
    }
  }
);

export const fetchProvider = createAsyncThunk(
  'provider/fetchProvider',
  async ({ practiceId, providerId }, { rejectWithValue }) => {
    try {
      const response = await providerService.getProvider(practiceId, providerId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.status === 404 
        ? 'Provider not found or has been deleted'
        : getProviderErrorMessage(error, 'Failed to fetch provider');
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchProviders = createAsyncThunk(
  'provider/fetchProviders',
  async ({ practiceId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await providerService.getProviders(practiceId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(getProviderErrorPayload(error, 'Failed to fetch providers'));
    }
  }
);

export const updateProvider = createAsyncThunk(
  'provider/updateProvider',
  async ({ practiceId, providerId, providerData }, { rejectWithValue }) => {
    try {
      const response = await providerService.updateProvider(practiceId, providerId, providerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getProviderErrorPayload(error, 'Failed to update provider'));
    }
  }
);

export const deleteProvider = createAsyncThunk(
  'provider/deleteProvider',
  async ({ practiceId, providerId }, { rejectWithValue }) => {
    try {
      const response = await providerService.deleteProvider(practiceId, providerId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.status === 404 
        ? 'Provider not found or has already been deleted'
        : getProviderErrorMessage(error, 'Failed to delete provider');
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  providers: [],
  currentProvider: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

const providerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProvider: (state) => {
      state.currentProvider = null;
    },
    resetState: (state) => {
      return { ...initialState };
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Provider
      .addCase(createProvider.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createProvider.fulfilled, (state, action) => {
        state.isCreating = false;
        
        // Ensure providers array exists
        if (!state.providers) {
          state.providers = [];
        }
        
        // Add the new provider to the beginning of the array
        const providerData = action.payload.data || action.payload;
        state.providers.unshift(providerData);
        
        state.error = null;
      })
      .addCase(createProvider.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      
      // Fetch Provider (single)
      .addCase(fetchProvider.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProvider.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProvider = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(fetchProvider.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Fetch Providers
      .addCase(fetchProviders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.providers = action.payload.data || [];
        state.error = null;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Update Provider
      .addCase(updateProvider.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        state.isUpdating = false;
        const updatedProvider = action.payload.data || action.payload;
        if (state.providers && Array.isArray(state.providers)) {
          const index = state.providers.findIndex(provider => provider.id === updatedProvider.id);
          if (index !== -1) {
            state.providers[index] = { ...state.providers[index], ...updatedProvider };
          }
        }
        if (
          state.currentProvider &&
          String(state.currentProvider.id) === String(updatedProvider.id)
        ) {
          state.currentProvider = { ...state.currentProvider, ...updatedProvider };
        }
        state.error = null;
      })
      .addCase(updateProvider.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      
      // Delete Provider
      .addCase(deleteProvider.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProvider.fulfilled, (state, action) => {
        state.isDeleting = false;
        if (state.providers && Array.isArray(state.providers)) {
          state.providers = state.providers.filter(provider => provider.id !== action.meta.arg.providerId);
        }
        state.error = null;
      })
      .addCase(deleteProvider.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  }
});

export const {
  clearError,
  clearCurrentProvider,
  resetState
} = providerSlice.actions;

export default providerSlice.reducer;
